import {
  AudioLines,
  Copy,
  Download,
  FileAudio,
  Hand,
  Hash,
  Headphones,
  Languages,
  MicVocal,
  Settings2,
  Pause,
  PhoneOff,
  Play,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { cn } from '@/lib/utils/cn';
import { usePromptAudio } from '../use-audio-preview';
import {
  CATEGORY_LABEL,
  promptStatusLabel,
  promptStatusTone,
  type PromptCategory,
  type PromptRow,
} from '../types';

/** Per-category icon + accent tint. */
const CATEGORY_VISUAL: Record<
  PromptCategory,
  { icon: LucideIcon; bg: string; fg: string }
> = {
  welcome: { icon: Hand, bg: 'bg-emerald-100', fg: 'text-emerald-700' },
  menu: { icon: Hash, bg: 'bg-violet-100', fg: 'text-violet-700' },
  noinput: { icon: Headphones, bg: 'bg-amber-100', fg: 'text-amber-700' },
  wronginput: { icon: AudioLines, bg: 'bg-rose-100', fg: 'text-rose-700' },
  thanks: { icon: Sparkles, bg: 'bg-sky-100', fg: 'text-sky-700' },
  tts: { icon: MicVocal, bg: 'bg-fuchsia-100', fg: 'text-fuchsia-700' },
  noagent: { icon: PhoneOff, bg: 'bg-slate-200', fg: 'text-slate-700' },
};

const STATUS_DOT: Record<
  'success' | 'warning' | 'danger' | 'muted',
  string
> = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-rose-500',
  muted: 'bg-slate-400',
};

function formatWhen(iso: string | undefined): string {
  if (!iso) return '—';
  // Server sends "YYYY-MM-DD HH:MM:SS" — keep the date + time at minute
  // precision; full ISO would be too noisy in a dense list.
  return iso.slice(0, 16).replace('T', ' ');
}

function accountTypeLabel(t: number | undefined): string {
  if (t === 0) return 'Promotional';
  if (t === 1) return 'Transactional';
  return '—';
}

interface PromptListProps {
  prompts: PromptRow[];
  isLoading?: boolean;
  selectedIds?: Set<number>;
  onSelectChange?: (id: number, next: boolean) => void;
  onSelectAll?: (next: boolean) => void;
  showApproveActions?: boolean;
  onApprove?: (p: PromptRow) => void;
  onReject?: (p: PromptRow) => void;
  onDelete?: (p: PromptRow) => void;
  onCopyUrl?: (p: PromptRow) => void;
  onDownload?: (p: PromptRow) => void;
  onTranscribe?: (p: PromptRow) => void;
  emptyTitle?: string;
  emptyDescription?: string;
}

export function PromptList({
  prompts,
  isLoading,
  selectedIds,
  onSelectChange,
  onSelectAll,
  showApproveActions,
  onApprove,
  onReject,
  onDelete,
  onCopyUrl,
  onDownload,
  onTranscribe,
  emptyTitle = 'No prompts',
  emptyDescription = 'Add a prompt to get started.',
}: PromptListProps) {
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-3 border-b border-border last:border-b-0 px-3 py-2.5"
          >
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-3 flex-1 max-w-xs" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-32" />
          </div>
        ))}
      </div>
    );
  }

  if (prompts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card/50 py-12 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <FileAudio className="h-6 w-6" />
        </div>
        <div className="text-sm font-semibold text-foreground">
          {emptyTitle}
        </div>
        <p className="mt-1 max-w-sm text-xs text-muted-foreground">
          {emptyDescription}
        </p>
      </div>
    );
  }

  const allSelected =
    onSelectChange != null &&
    prompts.length > 0 &&
    prompts.every((p) => selectedIds?.has(p.promptId));

  /* ---- Role-driven column visibility (mirrors legacy ListVoiceFiles) ----
   *
   * SADMIN/sub-super: Voice File Id, File Name, File Type, Status, Uploaded By,
   *   Account, Upload Time, Approval Time, Deletion Date, Deleted By, Remarks, Action
   * ROLE_ADMIN (reseller): Voice File Id, File Name, File Type, Status, Uploaded By,
   *   Account, Remarks, Action
   * ROLE_USER:            Voice File Id, File Name, File Type, Status,
   *   Account, Remarks, Action
   *
   * Data-driven fallback: SADMIN date columns auto-hide if no row carries
   * date data (the per-user endpoint omits `upload_date` until the backend
   * DAO redeploys).
   */
  const role = useAuthStore((s) => s.role);
  const isSadminLike =
    role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;
  const isReseller = role === USER_ROLES.ADMIN;
  const showUploader = isSadminLike || isReseller;
  const showUploaded =
    isSadminLike &&
    prompts.some((p) => Boolean(p.uploadTime) || Boolean(p.approvalTime));
  const showApproval =
    isSadminLike && prompts.some((p) => Boolean(p.approvalTime));
  const showDeletion =
    isSadminLike && prompts.some((p) => Boolean(p.delete_date));
  const showDeletedBy =
    isSadminLike && prompts.some((p) => Boolean(p.deletedBy));

  // Build the grid template by composing column widths in order. Done as an
  // inline `style` rather than a Tailwind arbitrary class because Tailwind's
  // JIT can't compile class strings that are assembled at runtime.
  const gridTemplateColumns = [
    '28px',                       // select
    '28px',                       // play
    '90px',                       // Voice File Id
    'minmax(0, 1.6fr)',           // File Name
    '110px',                      // File Type
    '100px',                      // Status
    showUploader ? '110px' : '',  // Uploaded By
    '110px',                      // Account
    showUploaded ? '140px' : '',  // Upload Time
    showApproval ? '140px' : '',  // Approval Time
    showDeletion ? '140px' : '',  // Deletion Date
    showDeletedBy ? '110px' : '', // Deleted By
    'minmax(0, 1.6fr)',           // Remarks
    '120px',                      // Actions
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      {/* Header row */}
      <div
        className="grid items-center gap-3 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
        style={{ gridTemplateColumns }}
      >
        {onSelectChange ? (
          <Checkbox
            checked={allSelected}
            onCheckedChange={(v) => onSelectAll?.(Boolean(v))}
            aria-label="Select all"
          />
        ) : (
          <span />
        )}
        <span />
        <span>Voice File Id</span>
        <span>File Name</span>
        <span>File Type</span>
        <span>Status</span>
        {showUploader && <span>Uploaded By</span>}
        <span>Account</span>
        {showUploaded && <span>Upload Time</span>}
        {showApproval && <span>Approval Time</span>}
        {showDeletion && <span>Deletion Date</span>}
        {showDeletedBy && <span>Deleted By</span>}
        <span>Remarks</span>
        <span className="text-right">Actions</span>
      </div>

      {/* Rows */}
      {prompts.map((p) => (
        <PromptRowItem
          key={p.promptId}
          prompt={p}
          selected={selectedIds?.has(p.promptId)}
          gridTemplateColumns={gridTemplateColumns}
          showUploader={showUploader}
          showUploaded={showUploaded}
          showApproval={showApproval}
          showDeletion={showDeletion}
          showDeletedBy={showDeletedBy}
          onSelectChange={
            onSelectChange ? (v) => onSelectChange(p.promptId, v) : undefined
          }
          showApproveActions={showApproveActions}
          onApprove={onApprove ? () => onApprove(p) : undefined}
          onReject={onReject ? () => onReject(p) : undefined}
          onDelete={onDelete ? () => onDelete(p) : undefined}
          onCopyUrl={onCopyUrl ? () => onCopyUrl(p) : undefined}
          onDownload={onDownload ? () => onDownload(p) : undefined}
          onTranscribe={onTranscribe ? () => onTranscribe(p) : undefined}
        />
      ))}
    </div>
  );
}

interface PromptRowItemProps {
  prompt: PromptRow;
  selected?: boolean;
  gridTemplateColumns: string;
  showUploader: boolean;
  showUploaded: boolean;
  showApproval: boolean;
  showDeletion: boolean;
  showDeletedBy: boolean;
  onSelectChange?: (next: boolean) => void;
  showApproveActions?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  onDelete?: () => void;
  onCopyUrl?: () => void;
  onDownload?: () => void;
  onTranscribe?: () => void;
}

function PromptRowItem({
  prompt,
  selected,
  gridTemplateColumns,
  showUploader,
  showUploaded,
  showApproval,
  showDeletion,
  showDeletedBy,
  onSelectChange,
  showApproveActions,
  onApprove,
  onReject,
  onDelete,
  onCopyUrl,
  onDownload,
  onTranscribe,
}: PromptRowItemProps) {
  const cat = ((prompt.promptCategory as PromptCategory) in CATEGORY_VISUAL
    ? (prompt.promptCategory as PromptCategory)
    : 'menu') as PromptCategory;
  const visual = CATEGORY_VISUAL[cat];
  const Icon = visual.icon;
  const tone = promptStatusTone(prompt.promptStatus);

  const audio = usePromptAudio(
    `prompt-${prompt.promptId}`,
    prompt.promptUrl,
  );

  const totalForBar =
    audio.duration > 0 ? audio.duration : prompt.promptDuration ?? 0;
  const progressPct =
    totalForBar > 0
      ? Math.min(100, (audio.progress / totalForBar) * 100)
      : 0;

  return (
    <div
      className={cn(
        'group/row relative grid items-center gap-3 border-b border-border px-3 py-2 transition-colors last:border-b-0 hover:bg-muted/30',
        selected && 'bg-primary/5',
        audio.playing && 'bg-primary/5',
      )}
      style={{ gridTemplateColumns }}
    >
      {/* Audio progress bar — sits along the bottom of the row when playing. */}
      {audio.playing && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-primary/70"
          style={{ width: `${progressPct}%` }}
        />
      )}

      {/* Checkbox */}
      {onSelectChange ? (
        <Checkbox
          checked={selected ?? false}
          onCheckedChange={(v) => onSelectChange(Boolean(v))}
          aria-label={`Select ${prompt.fileName}`}
        />
      ) : (
        <span />
      )}

      {/* Play button */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'h-7 w-7 shrink-0 rounded-full',
          audio.playing && 'bg-primary text-primary-foreground hover:bg-primary',
        )}
        onClick={audio.toggle}
        disabled={!prompt.promptUrl}
        aria-label={audio.playing ? 'Pause' : 'Play'}
      >
        {audio.playing ? (
          <Pause className="h-3.5 w-3.5" />
        ) : (
          <Play className="h-3.5 w-3.5" />
        )}
      </Button>

      {/* Voice File Id */}
      <span className="font-mono text-xs text-muted-foreground" title={String(prompt.promptId)}>
        {prompt.promptId}
      </span>

      {/* File Name */}
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-foreground" title={prompt.fileName}>
          {prompt.fileName || prompt.promptName || `#${prompt.promptId}`}
        </div>
      </div>

      {/* File Type (category) */}
      <span
        className={cn(
          'inline-flex w-fit items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          visual.bg,
          visual.fg,
        )}
      >
        <Icon className="h-3 w-3" />
        {CATEGORY_LABEL[cat]}
      </span>

      {/* Status with dot */}
      <span className="inline-flex items-center gap-1.5 text-xs text-foreground">
        <span
          aria-hidden
          className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[tone])}
        />
        {promptStatusLabel(prompt.promptStatus)}
      </span>

      {/* Uploaded By — admin tiers only (omitted for ROLE_USER per legacy) */}
      {showUploader && (
        <span className="truncate text-xs text-muted-foreground" title={prompt.username ?? ''}>
          {prompt.username ?? `User #${prompt.userId ?? '—'}`}
        </span>
      )}

      {/* Account */}
      <span className="text-xs text-foreground">
        {accountTypeLabel(prompt.accountType as number | undefined)}
      </span>

      {/* Upload Time — SADMIN only, data-driven */}
      {showUploaded && (
        <span className="font-mono text-[11px] text-muted-foreground">
          {formatWhen(prompt.uploadTime)}
        </span>
      )}

      {/* Approval Time — SADMIN only, data-driven */}
      {showApproval && (
        <span className="font-mono text-[11px] text-muted-foreground">
          {formatWhen(prompt.approvalTime)}
        </span>
      )}

      {/* Deletion Date — SADMIN only, data-driven */}
      {showDeletion && (
        <span className="font-mono text-[11px] text-muted-foreground">
          {formatWhen(prompt.delete_date)}
        </span>
      )}

      {/* Deleted By — SADMIN only, data-driven */}
      {showDeletedBy && (
        <span className="truncate text-xs text-muted-foreground">
          {prompt.deletedBy ?? '—'}
        </span>
      )}

      {/* Remarks */}
      <span
        className="truncate text-xs text-foreground"
        title={prompt.remarks ?? ''}
      >
        {prompt.remarks ? (
          prompt.remarks
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </span>

      {/* Actions */}
      <div className="flex items-center justify-end gap-1">
        {showApproveActions && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-rose-700 opacity-0 group-hover/row:opacity-100"
              onClick={onReject}
              aria-label="Reject"
              title="Reject"
            >
              <ThumbsDown className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-emerald-700 opacity-0 group-hover/row:opacity-100"
              onClick={onApprove}
              aria-label="Approve"
              title="Approve"
            >
              <ThumbsUp className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label="Row actions"
            >
              <Settings2 className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="truncate">
              {prompt.fileName}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {onCopyUrl && (
              <DropdownMenuItem
                onClick={onCopyUrl}
                disabled={!prompt.promptUrl}
              >
                <Copy className="h-4 w-4" />
                Copy audio URL
              </DropdownMenuItem>
            )}
            {onDownload && (
              <DropdownMenuItem
                onClick={onDownload}
                disabled={!prompt.promptUrl}
              >
                <Download className="h-4 w-4" />
                Download
              </DropdownMenuItem>
            )}
            {onTranscribe && (
              <DropdownMenuItem
                onClick={onTranscribe}
                disabled={!prompt.promptUrl}
              >
                <Languages className="h-4 w-4" />
                Transcribe to Hindi
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
